/**
 * @file taskTransitions.js
 * @description Centralized helper utility for mapping backend-computed
 *              workflow transition and permission rules to UI components.
 *              Used by both task details and row listing pages (OA Shells and PGS).
 */

const MANAGEMENT_ROLES = ["Admin", "Application Manager"];

/**
 * Check if the Processor dropdown is locked for a given status.
 * @param {string} status - Current task status
 * @returns {boolean}
 */
export const isProcessorLocked = (status) => {
    return ['In Progress', 'Assign QA', 'QC Pending', 'Under Review', 'Completed'].includes(status);
};

/**
 * Check if the QA dropdown is locked for a given status.
 * @param {string} status - Current task status
 * @returns {boolean}
 */
export const isQaLocked = (status) => {
    return ['QC Pending', 'Under Review', 'Completed'].includes(status);
};

/**
 * Filter eligible users for a given role (Processor or QA).
 * Resolves delegation roles via canActAs.
 * @param {Array} users - All workflow users
 * @param {string} targetRoleName - "Processor" or "QA"
 * @returns {Array}
 */
// COL-137: Processor and QA dropdowns share the same candidate set —
// all internal workflow users (Admin, Application Manager, Processor, QA).
const ASSIGNABLE_ROLE_NAMES = ["Admin", "Application Manager", "Processor", "QA"];

export const getEligibleUsers = (users, targetRoleName) => {
    // targetRoleName retained for call-site compatibility; both Processor and
    // QA dropdowns now return the same unified set.
    return users?.filter(u => ASSIGNABLE_ROLE_NAMES.includes(u.role?.roleName)) || [];
};

/**
 * Get status options available in the inline DROPDOWN (PGS).
 * Excludes submitOnly transitions.
 *
 * @param {string} currentStatus - Task's current status
 * @param {string} role - User's primary role
 * @param {Object} task - Task object (needs processorId, qaId)
 * @param {number} oaUserId - The current user's OA Shell user ID
 * @returns {string[]} Array of valid target statuses
 */
export const getDropdownOptions = (currentStatus, role, task, oaUserId) => {
    if (!role) return [];

    const isAdminOrAM = MANAGEMENT_ROLES.includes(role);

    if (role === "Processor") {
        if (currentStatus === "To Do") {
            // Processor: dropdown enabled only if assigned to them
            if (task?.processorId && parseInt(task.processorId) === parseInt(oaUserId)) {
                return ["In Progress"];
            }
        }
    }

    if (role === "QA" || isAdminOrAM) {
        if (currentStatus === "QC Pending") {
            // Dropdown disabled if no QA assigned (matches OA Shell)
            if (task?.qaId && (isAdminOrAM || parseInt(task.qaId) === parseInt(oaUserId))) {
                return ["Under Review"];
            }
        }
    }

    return [];
};

/**
 * Get the target status for the SUBMIT button (PGS).
 * Returns null if no submit transition is available.
 *
 * @param {string} currentStatus
 * @param {string} role
 * @param {Object} task
 * @param {number} oaUserId
 * @returns {string|null}
 */
export const getSubmitTransition = (currentStatus, role, task, oaUserId) => {
    if (!role) return null;

    const isAdminOrAM = MANAGEMENT_ROLES.includes(role);

    // To Do → In Progress: Admin/AM can submit when processor is assigned
    if (isAdminOrAM) {
        if (currentStatus === "To Do" && task?.processorId) {
            return "In Progress";
        }
    }

    if (role === "Processor" || isAdminOrAM) {
        if (currentStatus === "In Progress") {
            if (isAdminOrAM || (task?.processorId && parseInt(task.processorId) === parseInt(oaUserId))) {
                return task?.qaId ? "QC Pending" : "Assign QA";
            }
        }
    }

    if (role === "QA" || isAdminOrAM) {
        if (currentStatus === "Under Review") {
            if (isAdminOrAM || (task?.qaId && parseInt(task.qaId) === parseInt(oaUserId))) {
                return "Completed";
            }
        }
    }

    return null;
};

/**
 * Whether the package requirement for the *Processor* submit step is satisfied.
 *
 * COL-158: the backend marks a task `packageRequired: false` when its client is
 * configured as upload-optional (Post Grant Support only). That exemption covers
 * the Processor hand-off to QA (QC Pending / Assign QA); the QA completion step
 * keeps its gate, so "Completed" still needs an uploaded file.
 *
 * @param {Object} task - Task with `packageRequired` and `submitTransitions`
 * @param {boolean} hasPackages - Whether at least one package exists
 * @returns {boolean}
 */
export const hasRequiredPackages = (task, hasPackages) => {
    if (hasPackages) return true;
    if (!task || task.packageRequired !== false) return false;
    const submitTransitions = task.submitTransitions || [];
    return submitTransitions.includes('QC Pending') || submitTransitions.includes('Assign QA');
};

/**
 * Check if the user can upload packages for this task.
 * Supports both OA Shell signature (one argument) and PGS signature (four arguments).
 *
 * @param {any} first - Task object or user Role
 * @param {string} [currentStatus]
 * @param {Object} [task]
 * @param {number} [oaUserId]
 * @returns {boolean}
 */
export const canUploadFile = (first, currentStatus, task, oaUserId) => {
    if (currentStatus === undefined) {
        // OA Shell signature: canUploadFile(task)
        const taskObj = first;
        return taskObj?.allowedActions?.includes('upload') || false;
    }

    // PGS signature: canUploadFile(role, currentStatus, task, oaUserId)
    const role = first;
    if (role === "Admin" || role === "Application Manager") {
        return currentStatus !== "Completed";
    }
    // COL-137: assignment confers capability — check whichever slot this user
    // actually holds on the task, not their primary role string. A user whose
    // primary role is Processor but is assigned QA here (or vice versa) must
    // still be able to upload during their turn.
    const isAssignedProcessor = task?.processorId && parseInt(task.processorId) === parseInt(oaUserId);
    const isAssignedQa = task?.qaId && parseInt(task.qaId) === parseInt(oaUserId);
    if (isAssignedProcessor && currentStatus === "In Progress") return true;
    if (isAssignedQa && currentStatus === "Under Review") return true;
    return false;
};

/**
 * Check if the Submit button should be enabled.
 * Supports both OA Shell signature and PGS signature.
 *
 * @param {any} first - Task object or current status string
 * @param {any} second - hasPackages boolean or role string
 * @param {any} [third] - userRole string or task object
 * @param {any} [fourth] - localProcessorId or oaUserId
 * @param {any} [fifth] - localQaId or hasPackages boolean
 * @returns {boolean}
 */
export const isSubmitEnabled = (first, second, third, fourth, fifth) => {
    if (typeof first === 'object') {
        // OA Shell signature: isSubmitEnabled(task, hasPackages, userRole, localProcessorId, localQaId)
        const task = first;
        const hasPackages = second;
        const localProcessorId = fourth;
        const localQaId = fifth;

        if (!task) return false;

        if (task.canAdminAssign) {
            if (task.taskStatus === 'Assign QA') {
                return !!(localQaId || task.qaId);
            }
            if (localProcessorId === 'SKIP_PROCESSING') {
                return !!localQaId;
            }
            // To Do: require a processor AND at least one assignment must
            // differ from persisted values (prevents re-submit after saving)
            if (!localProcessorId) return false;
            const procChanged = String(localProcessorId) !== String(task.processorId || '');
            const qaChanged = String(localQaId || '') !== String(task.qaId || '');
            return procChanged || qaChanged;
        }

        const submitTransitions = task.submitTransitions || [];
        const needsPackages = submitTransitions.includes('QC Pending') || submitTransitions.includes('Completed');
        if (needsPackages) return hasPackages;

        return submitTransitions.length > 0;
    } else {
        // PGS signature: isSubmitEnabled(currentStatus, role, task, oaUserId, hasPackages)
        const currentStatus = first;
        const role = second;
        const task = third;
        const oaUserId = fourth;
        const hasPackages = fifth;

        const submitTarget = getSubmitTransition(currentStatus, role, task, oaUserId);
        if (!submitTarget) return false;
        
        // Admin/AM can submit unconditionally (safety override)
        const isAdminOrAM = MANAGEMENT_ROLES.includes(role);
        if (isAdminOrAM) return true;

        return hasPackages;
    }
};

/**
 * Get the disabled reason tooltip text for the Submit button.
 * Supports both OA Shell signature and PGS signature.
 *
 * @param {any} first - Task object or current status string
 * @param {any} second - hasPackages boolean or role string
 * @param {any} [third] - userRole string or task object
 * @param {any} [fourth] - localProcessorId or oaUserId
 * @param {any} [fifth] - localQaId or hasPackages boolean
 * @returns {string}
 */
export const getSubmitDisabledReason = (first, second, third, fourth, fifth) => {
    if (typeof first === 'object') {
        // OA Shell signature: getSubmitDisabledReason(task, hasPackages, userRole, localProcessorId, localQaId)
        const task = first;
        const hasPackages = second;
        const userRole = third;
        const localProcessorId = fourth;
        const localQaId = fifth;

        if (!task) return "";

        if (task.canAdminAssign) {
            if (task.taskStatus === 'Assign QA' && !(localQaId || task.qaId)) {
                return "QA assignment is required to submit";
            }
            if (localProcessorId === 'SKIP_PROCESSING' && !localQaId) {
                return "QA assignment is required to skip processing";
            }
            if (!localProcessorId && task.taskStatus === 'To Do') {
                return "Processor assignment is required to submit";
            }
            // To Do: if assignments are already saved and nothing changed, show reason
            if (task.taskStatus === 'To Do' && localProcessorId && localProcessorId !== 'SKIP_PROCESSING') {
                const procChanged = String(localProcessorId) !== String(task.processorId || '');
                const qaChanged = String(localQaId || '') !== String(task.qaId || '');
                if (!procChanged && !qaChanged) {
                    return "Assignments already saved";
                }
            }
            return "";
        }

        const submitTransitions = task.submitTransitions || [];
        if (submitTransitions.length === 0) {
            if (task.taskStatus === "Completed") return "Task is already completed";
            if (task.taskStatus === "QC Pending" && userRole === "Processor") return "Task is pending QA review";
            return "No submit action available";
        }

        const needsPackages = submitTransitions.includes('QC Pending') || submitTransitions.includes('Completed');
        if (needsPackages && !hasPackages) {
            return "Upload at least one file before submitting";
        }
        return "";
    } else {
        // PGS signature: getSubmitDisabledReason(currentStatus, role, task, oaUserId, hasPackages)
        const currentStatus = first;
        const role = second;
        const task = third;
        const oaUserId = fourth;
        const hasPackages = fifth;

        const submitTarget = getSubmitTransition(currentStatus, role, task, oaUserId);
        if (!submitTarget) {
            if (currentStatus === "Completed") return "Task is already completed";
            if (currentStatus === "To Do") return "Assign a Processor to enable submission";
            if (currentStatus === "QC Pending" && role === "Processor") return "Task is pending QA review";
            return "No submit action available";
        }
        const isAdminOrAM = MANAGEMENT_ROLES.includes(role);
        if (!isAdminOrAM && !hasPackages) return "Upload at least one file before submitting";
        return "";
    }
};

/**
 * Get the confirmation message for a submit action.
 * Supports both OA Shell signature and PGS signature.
 *
 * @param {string} submitTarget - The target status
 * @param {Object} task
 * @param {string|number|null} [localQaId] - staged QA selection
 * @returns {string}
 */
export const getSubmitConfirmMessage = (submitTarget, task, localQaId) => {
    if (submitTarget === "In Progress") {
        return "Are you sure you want to assign and submit this task?";
    }
    if (submitTarget === "QC Pending" || submitTarget === "Assign QA") {
        const isSkipProcessing = task?.taskStatus === "To Do" && (!task?.processorId || task?.processorName === "Processing Skipped");
        if (isSkipProcessing) {
            return "Skip processing and assign directly to QA? The task will move to QA stage.";
        }
        const hasQa = localQaId || task?.qaId;
        if (!hasQa) {
            return 'Submit this task? It will be moved to "Assign QA" since no QA is assigned.';
        }
        return "Submit this task for QC review?";
    }
    if (submitTarget === "Completed") {
        if (task?.workType === "Post Grant Support") {
            return "Submit this task? This will mark it as Completed.";
        }
        return "Submit this task? This will lock all observations and mark it as Completed.";
    }
    return "Are you sure you want to submit this task?";
};
